import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import SEO from "../../components/base/SEO";

const services = [
  {
    icon: "ri-snowy-line",
    title: "Central AC Installation & Replacement",
    desc: "New high-efficiency central air conditioning installation — including full system sizing, load calculations, and Energy Star-qualified equipment.",
  },
  {
    icon: "ri-tools-line",
    title: "AC Repair & Diagnostics",
    desc: "Fast diagnostics and same-day repair on all makes and models. Our technicians carry the most common parts on every truck for quick fixes.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Annual AC Tune-Ups & Maintenance",
    desc: "Pre-season maintenance to maximize cooling efficiency, extend equipment life, and catch small issues before they become expensive breakdowns.",
  },
  {
    icon: "ri-temp-cold-line",
    title: "Ductless Mini Split Cooling",
    desc: "Zone-based cooling without ductwork — perfect for room additions, sunrooms, and homes without existing duct systems.",
  },
  {
    icon: "ri-home-gear-line",
    title: "Ductwork Design & Installation",
    desc: "Properly designed and sealed ductwork ensures every room cools evenly and efficiently without wasting energy.",
  },
  {
    icon: "ri-windy-line",
    title: "Indoor Air Quality Solutions",
    desc: "Whole-home dehumidifiers, air purifiers, and advanced filtration systems that work alongside your AC for cleaner, healthier air.",
  },
];

const systemTypes = [
  {
    name: "Central Air Conditioners",
    tagline: "The most common cooling system in CT",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/f77e8f76e3f22302514c9372442cbbe3.jpeg",
    desc: "Modern central AC units achieve up to 26 SEER2 efficiency ratings, dramatically cutting cooling costs compared to older equipment. We install, replace, and service all major brands with precise sizing for your home.",
    features: ["Up to 26 SEER2 efficiency", "Variable-speed compressor", "Energy Star qualified", "Single & two-stage options"],
    highlight: false,
  },
  {
    name: "Ductless Mini Split AC",
    tagline: "Cool any room without ductwork",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/c4a30da68f0fcf12a1a7aa9dafff163d.jpeg",
    desc: "Ductless mini splits provide powerful, efficient cooling for individual rooms or entire homes — no ductwork required. Each zone has its own thermostat for personalized comfort.",
    features: ["No ductwork needed", "Zone-by-zone control", "Ultra-quiet operation", "One-day installation"],
    highlight: true,
  },
  {
    name: "Heat Pump Units",
    tagline: "Year-round heating & cooling in one",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/3dd9ecd42a17e4f83098eadf7b39809a.jpeg",
    desc: "A heat pump cools your home in summer and heats it in winter — eliminating the need for separate furnace and AC systems. Modern cold-climate models from Mitsubishi maintain full efficiency even in CT summers.",
    features: ["Heats & cools in one unit", "Up to 30% federal tax credit", "Highest efficiency available", "Works with existing ductwork"],
    highlight: false,
  },
  {
    name: "Packaged Rooftop Units",
    tagline: "All-in-one commercial cooling solution",
    image: "https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/aac312fc1474c5b566989b7772e0dd1a.jpeg",
    desc: "Self-contained packaged units for commercial applications — combining cooling, heating, and ventilation in one cabinet. Ideal for retail, offices, and light industrial buildings.",
    features: ["3–50 ton capacity", "Gas/electric or heat pump", "High-static duct capability", "Economizer & fresh air intake"],
    highlight: false,
  },
];

const brands = [
  { name: "Carrier", note: "Factory Authorized Dealer — Infinity & Performance series" },
  { name: "Mitsubishi", note: "Diamond Dealer — Hyper Heat cold-climate & cooling systems" },
  { name: "Crown", note: "Gold Partner — high-efficiency ducted & ductless" },
  { name: "Bosch", note: "Certified — IDS Premium with exceptional SEER2 ratings" },
  { name: "Lennox", note: "Authorized — Elite & Merit series air conditioners" },
];

const faqs = [
  {
    q: "When should I replace my AC instead of repairing it?",
    a: "If your air conditioner is over 10–12 years old and the repair costs exceed 50% of a new system, replacement is usually the smarter financial move. We'll give you an honest assessment — not just the option that makes us more money.",
  },
  {
    q: "How much does a new central AC installation cost in Connecticut?",
    a: "Most central AC replacements run $3,500–$7,500 installed, depending on equipment size, SEER2 efficiency rating, and installation complexity. We provide free written estimates with no obligation so you know exactly what to expect.",
  },
  {
    q: "What SEER2 rating should I look for in a new air conditioner?",
    a: "For Connecticut summers, we recommend 16 SEER2 or higher. Higher-efficiency units cost more upfront but pay for themselves in 3–5 years through lower electric bills, and they often qualify for Eversource and UI utility rebates.",
  },
  {
    q: "Do you service all brands of air conditioners?",
    a: "Yes — our technicians are factory-trained to service all major brands including Carrier, Lennox, Trane, Rheem, Bryant, Goodman, Mitsubishi, and more. We stock common parts on every truck to minimize return visits and get your AC running same-day.",
  },
  {
    q: "Are there rebates available for new AC equipment in Connecticut?",
    a: "Absolutely. Eversource and UI offer rebates on qualifying high-efficiency air conditioners and heat pumps. Federal tax credits also apply to heat pump cooling systems (up to $2,000). We help you identify and apply for every incentive you qualify for.",
  },
  {
    q: "How often should I have my air conditioning system serviced?",
    a: "Annual tune-ups are strongly recommended — ideally in late spring before cooling season kicks in. Regular maintenance extends equipment life, maintains peak efficiency, and keeps your manufacturer warranty valid. Our service contract plans make it affordable and hassle-free.",
  },
];

const siteUrl: string = (import.meta.env.VITE_SITE_URL as string) ?? "";

const coolingJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "AC Installation & Air Conditioning Repair Connecticut | Ultimate Heating & Cooling",
    description:
      "Expert AC installation, air conditioning repair, and cooling system service in Connecticut. Central AC, ductless mini splits, and commercial cooling. Free estimates — call (959) 241-8524.",
    url: `${siteUrl}/cooling`,
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
        { "@type": "ListItem", position: 2, name: "Cooling", item: `${siteUrl}/cooling` },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  },
];

export default function CoolingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="AC Installation & Air Conditioning Repair Connecticut | Ultimate Heating & Cooling"
        description="Expert AC installation, air conditioning repair, and cooling system service in Connecticut. Central AC, ductless mini splits, and commercial cooling systems. Free estimates available."
        keywords="AC installation Connecticut, air conditioning repair CT, cooling contractor Somers CT, HVAC air conditioner Connecticut"
        canonical="/cooling"
        jsonLd={coolingJsonLd}
      />
      <Navbar />

      {/* ── HERO ── */}
      <section className="relative w-full h-[520px] md:h-[600px] overflow-hidden">
        <img
          src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/b2784776976929c83756fcec95b848f1.jpeg"
          alt="Air conditioning installation Connecticut"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B2447]/95 via-[#0B2447]/85 to-[#0B2447]/35"></div>
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-6xl mx-auto px-6 w-full">
            <span className="inline-block bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.2em] px-3 py-1.5 rounded-full mb-5 drop-shadow-md">
              COOLING & AC SERVICES
            </span>
            <h1
              className="text-4xl md:text-5xl font-extrabold text-white leading-tight mb-5 max-w-2xl drop-shadow-lg"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Stay Cool All Summer —<br />Connecticut&apos;s AC Pros
            </h1>
            <p className="text-white text-base max-w-xl leading-relaxed mb-8 drop-shadow-md">
              From central AC installation to emergency air conditioning repair, Ultimate Heating &amp; Cooling keeps your Connecticut home comfortably cool — with fast response and honest pricing.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap w-fit"
              >
                GET A FREE ESTIMATE
              </Link>
              <a
                href="tel:9592418524"
                className="border border-white/60 text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap w-fit flex items-center gap-2"
              >
                <i className="ri-phone-line"></i>
                (959) 241-8524
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES GRID ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHAT WE DO</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Complete Air Conditioning Services for Your Home
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto mt-3 leading-relaxed">
              Whether you need emergency repair, a new energy-efficient system, or a pre-season tune-up — we handle everything cooling-related for Connecticut homeowners.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {services.map((s) => (
              <div key={s.title} className="bg-[#F0F4FA] rounded-xl p-6 border border-gray-100">
                <div className="w-11 h-11 flex items-center justify-center bg-[#C8102E]/10 rounded-xl mb-4">
                  <i className={`${s.icon} text-xl text-[#C8102E]`}></i>
                </div>
                <h3 className="font-bold text-[#0B2447] text-sm mb-2">{s.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SYSTEM TYPES ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">SYSTEMS WE INSTALL</span>
            <h2
              className="text-3xl font-extrabold text-[#0B2447] mt-2 mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              The Right Cooling System for Your Home
            </h2>
            <p className="text-gray-500 text-sm max-w-lg mx-auto leading-relaxed">
              We&apos;ll help you choose the best cooling solution based on your home size, existing ductwork, efficiency goals, and budget.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {systemTypes.map((sys) => (
              <div
                key={sys.name}
                className={`rounded-2xl overflow-hidden border-2 bg-white ${sys.highlight ? "border-[#C8102E]" : "border-gray-100"}`}
              >
                {sys.highlight && (
                  <div className="bg-[#C8102E] text-white text-[10px] font-bold tracking-[0.18em] text-center py-2">
                    MOST VERSATILE
                  </div>
                )}
                <div className="w-full h-52 overflow-hidden">
                  <img
                    src={sys.image}
                    alt={sys.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className="text-[#C8102E] text-[10px] font-bold tracking-[0.15em] uppercase">{sys.tagline}</span>
                  <h3
                    className="text-lg font-extrabold text-[#0B2447] mt-1 mb-3"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {sys.name}
                  </h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-4">{sys.desc}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {sys.features.map((f) => (
                      <div key={f} className="flex items-center gap-2">
                        <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 bg-[#C8102E]/10 rounded-full">
                          <i className="ri-check-line text-[#C8102E] text-[10px]"></i>
                        </span>
                        <span className="text-xs text-gray-600 leading-snug">{f}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="relative w-full h-[420px] rounded-2xl overflow-hidden">
              <img
                src="https://static.readdy.ai/image/36fff7c058435a92029a1d7ae462693c/2b2d6ad2a6612380fcd313e224d561c7.jpeg"
                alt="Ultimate Heating & Cooling AC technician"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute top-4 left-4 bg-white rounded-xl px-4 py-3">
                <p className="text-[#0B2447] text-xs font-bold">Factory-Trained Technicians</p>
                <p className="text-gray-500 text-[11px] mt-0.5">Certified &amp; Licensed in CT</p>
              </div>
            </div>
            <div>
              <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">WHY CHOOSE ULTIMATE</span>
              <h2
                className="text-3xl font-extrabold text-[#0B2447] mt-3 mb-5 leading-tight"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Cooling Specialists<br />You Can Count On
              </h2>
              <div className="space-y-4">
                {[
                  {
                    icon: "ri-verified-badge-line",
                    title: "Licensed & Factory-Certified",
                    desc: "Our technicians are factory-certified by Carrier, Mitsubishi, Daikin, and more — ensuring your warranty remains fully intact after every service call.",
                  },
                  {
                    icon: "ri-money-dollar-circle-line",
                    title: "Honest, Upfront Pricing",
                    desc: "We provide detailed written estimates before any work begins. No hidden fees, no surprise invoices — just clear pricing and quality work.",
                  },
                  {
                    icon: "ri-map-pin-line",
                    title: "Connecticut-Based & Local",
                    desc: "We know CT building codes, rebate programs, and the demands of New England summers. We live and work in the same communities we serve.",
                  },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#C8102E]/10 rounded-xl flex-shrink-0">
                      <i className={`${item.icon} text-lg text-[#C8102E]`}></i>
                    </div>
                    <div>
                      <h4 className="font-bold text-[#0B2447] text-sm mb-1">{item.title}</h4>
                      <p className="text-gray-500 text-xs leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── BRANDS ── */}
      <section className="py-16 bg-[#F0F4FA] border-y border-gray-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-10">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">BRANDS WE INSTALL & SERVICE</span>
            <h2
              className="text-xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Authorized Dealer & Certified Installer
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {brands.map((b) => (
              <div key={b.name} className="bg-white rounded-xl px-5 py-5 text-center border border-gray-100">
                <div className="w-9 h-9 flex items-center justify-center bg-[#0B2447]/10 rounded-lg mx-auto mb-3">
                  <i className="ri-award-fill text-[#0B2447] text-base"></i>
                </div>
                <p className="text-[#0B2447] font-bold text-sm mb-1">{b.name}</p>
                <p className="text-gray-400 text-[11px] leading-snug">{b.note}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REBATE BANNER ── */}
      <section className="py-14 bg-[#0B2447]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center gap-6 md:gap-10">
            <div className="w-16 h-16 flex items-center justify-center bg-[#C8102E]/20 rounded-2xl flex-shrink-0">
              <i className="ri-money-dollar-circle-fill text-[#C8102E] text-3xl"></i>
            </div>
            <div className="flex-1 text-center md:text-left">
              <p className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase mb-2">AVAILABLE REBATES & INCENTIVES</p>
              <h3
                className="text-2xl font-extrabold text-white mb-2"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                Save Up to $2,000 on Qualifying Cooling Equipment
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed">
                Eversource and UI offer rebates on high-efficiency air conditioners and heat pumps. The Inflation Reduction Act adds a 30% federal tax credit on qualifying heat pump cooling systems. We&apos;ll identify every incentive that applies to your project.
              </p>
            </div>
            <div className="flex-shrink-0">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-7 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                GET FREE ESTIMATE
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20 bg-[#F0F4FA]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">FAQ</span>
            <h2
              className="text-2xl font-extrabold text-[#0B2447] mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Air Conditioning Questions Answered
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer group"
                >
                  <span className="text-sm font-semibold text-[#0B2447] group-hover:text-[#C8102E] transition-colors pr-4 leading-snug">
                    {faq.q}
                  </span>
                  <span
                    className={`w-6 h-6 flex items-center justify-center rounded-full flex-shrink-0 transition-all duration-200 ${
                      openFaq === i ? "bg-[#C8102E] text-white rotate-45" : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    <i className="ri-add-line text-base"></i>
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-5 border-t border-gray-100">
                    <p className="text-gray-500 text-sm leading-relaxed pt-4">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="relative w-full overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=cool%20comfortable%20modern%20home%20interior%20bright%20sunny%20summer%20day%20air%20conditioning%20vent%20stylish%20living%20room%20natural%20light%20Connecticut%20residential%20interior%20relaxing%20atmosphere&width=1400&height=400&seq=cooling-cta-01&orientation=landscape"
          alt="Cool comfortable home Connecticut"
          className="w-full h-72 object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0B2447]/85"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center px-6">
            <span className="text-[#C8102E] text-xs font-bold tracking-[0.2em] uppercase">READY TO GET STARTED?</span>
            <h2
              className="text-3xl font-extrabold text-white mt-3 mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Beat the Heat — Call Connecticut&apos;s AC Pros
            </h2>
            <p className="text-white/70 text-sm mb-7 max-w-md mx-auto leading-relaxed">
              Free estimates on new systems. Fast repair response. Emergency service. Let&apos;s get your cooling sorted before the temperature spikes.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="bg-[#C8102E] text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-[#A8091D] transition-colors cursor-pointer whitespace-nowrap"
              >
                SCHEDULE FREE ESTIMATE
              </Link>
              <a
                href="tel:9592418524"
                className="border border-white/40 text-white font-bold text-sm px-8 py-3.5 rounded-full hover:bg-white/10 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                <i className="ri-phone-line"></i>
                CALL (959) 241-8524
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}